import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { FREQUENCIES, formatApiErrorDetail } from "@/lib/api";
import { PageContainer, PageHeader, SecondaryButton, PrimaryButton, Field, inputCls } from "@/components/ui-kit";
import { ChevronLeft, ChevronRight, ChevronDown, CalendarCheck, AlertCircle, Route, X } from "lucide-react";
import useTechnicians from "@/hooks/useTechnicians";
import { toast } from "sonner";

const MONTH_NAMES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function AutoScheduleModal({ year: initialYear, defaultMonth, onClose, onDone }) {
  const currentM = new Date().getMonth() + 1;
  const { items: technicians } = useTechnicians();
  const [year, setYearLocal] = useState(initialYear || new Date().getFullYear());
  const [month, setMonth] = useState(defaultMonth || currentM);
  const [technician, setTechnician] = useState("");
  const [technicianOther, setTechnicianOther] = useState(false);
  const [maxPerDay, setMaxPerDay] = useState(3);
  const [startTime, setStartTime] = useState("10:00");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/services/auto-schedule-month", {
        year: Number(year), month: Number(month), technician, max_per_day: Number(maxPerDay), start_time: startTime,
      });
      setResult(data);
      toast.success(`${data.created} visitas creadas`);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg border-t-4 border-[#FF5A00]" data-testid="auto-schedule-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Ruta automática</div>
            <div className="font-display font-bold text-xl">Repartir ruta del mes</div>
          </div>
          <button type="button" onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-xs text-neutral-500 leading-relaxed">
            Se generan servicios <b>programados</b> para todos los clientes activos que corresponden a este mes según su periodicidad.
            Se distribuyen en días hábiles (Lun–Vie) agrupando por comuna para optimizar la ruta.
            Los clientes que ya tienen visita agendada este mes se omiten.
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Año *">
              <select value={year} onChange={(e) => setYearLocal(Number(e.target.value))} className={inputCls} data-testid="auto-schedule-year">
                {[year - 1, year, year + 1, year + 2].filter((v, i, a) => a.indexOf(v) === i).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </Field>
            <Field label="Mes *">
              <select value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls} data-testid="auto-schedule-month">
                {MONTH_NAMES.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
              </select>
            </Field>
            <Field label="Máx. visitas por día *">
              <select value={maxPerDay} onChange={(e) => setMaxPerDay(e.target.value)} className={inputCls} data-testid="auto-schedule-maxperday">
                {[2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n} servicios/día</option>)}
              </select>
            </Field>
            <Field label="Técnico responsable">
              {(() => {
                const known = technicians.some((t) => t.name === technician);
                const otherMode = technicianOther || (!!technician && !known);
                const selectValue = otherMode ? "__other__" : (known ? technician : "");
                return (
                  <>
                    <select value={selectValue} onChange={(e) => {
                      const v = e.target.value;
                      if (v === "__other__") { setTechnicianOther(true); setTechnician(""); }
                      else { setTechnicianOther(false); setTechnician(v); }
                    }} className={inputCls} data-testid="auto-schedule-tech">
                      <option value="">— Sin asignar —</option>
                      {technicians.map((t) => (
                        <option key={t.id} value={t.name}>{t.name}{t.role === "admin" ? " (admin)" : ""}</option>
                      ))}
                      <option value="__other__">Otro (escribir nombre)…</option>
                    </select>
                    {otherMode && (
                      <input type="text" autoFocus value={technician} onChange={(e) => setTechnician(e.target.value)}
                        placeholder="Nombre del técnico externo"
                        className={`${inputCls} mt-2`} data-testid="auto-schedule-tech-other" />
                    )}
                  </>
                );
              })()}
            </Field>
            <Field label="Hora de inicio">
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={inputCls} />
            </Field>
          </div>

          {result && (
            <div className="mt-4 border border-[#00C853]/30 bg-[#00C853]/10 p-3 text-sm">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-[#008C3A] mb-2">Resultado</div>
              <div><b>{result.created}</b> visitas creadas · <b>{result.unassigned}</b> sin asignar (revisa capacidad).</div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <SecondaryButton type="button" onClick={onClose}>{result ? "Cerrar" : "Cancelar"}</SecondaryButton>
          {!result && (
            <PrimaryButton type="submit" disabled={saving} data-testid="auto-schedule-submit">
              <Route className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />{saving ? "Repartiendo…" : "Repartir ruta"}
            </PrimaryButton>
          )}
        </div>
      </form>
    </div>
  );
}

function MonthCard({ m, year, isCurrent, expanded, onToggle }) {
  const scheduledIds = new Set(m.scheduled.map((s) => s.customer_id));
  const totalUf = m.expected.reduce((a, b) => a + (b.price_uf || 0), 0);
  const scheduledCount = m.expected.filter((c) => scheduledIds.has(c.customer_id)).length;
  const pendingCount = m.expected.length - scheduledCount;

  return (
    <div
      data-testid={`billing-month-${m.month}`}
      className={`bg-white border ${isCurrent ? "border-[#FF5A00] ring-1 ring-[#FF5A00]" : "border-neutral-200"}`}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`billing-toggle-${m.month}`}
        className={`w-full px-4 py-3 border-b border-neutral-200 flex items-center justify-between text-left ${isCurrent ? "bg-[#FF5A00] text-white" : "bg-neutral-50 hover:bg-neutral-100"}`}
      >
        <div className="flex items-center gap-3">
          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "" : "-rotate-90"}`} />
          <div className="font-display font-bold">{MONTH_NAMES[m.month - 1]}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`font-mono-kpi text-[10px] uppercase tracking-[0.15em] ${isCurrent ? "text-white" : "text-neutral-500"}`}>
            {m.expected.length} flujo{m.expected.length === 1 ? "" : "s"}
          </div>
          {m.expected.length > 0 && (
            <div className={`font-mono-kpi text-[10px] uppercase ${isCurrent ? "text-white" : "text-neutral-600"}`}>
              <span className="text-[#00C853]">{scheduledCount}</span>
              <span className="opacity-60"> / {pendingCount} pend</span>
            </div>
          )}
        </div>
      </button>
      {expanded && (
        <>
          <div className="p-4">
            {m.expected.length === 0 ? (
              <div className="text-xs text-neutral-400 text-center py-4">— sin servicios esperados —</div>
            ) : (
              <ul className="space-y-2">
                {m.expected.map((c, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <Link to={`/admin/clientes/${c.customer_id}`} data-testid={`billing-customer-${c.customer_id}-m${m.month}`} className="min-w-0 flex-1 hover:text-[#FF5A00]">
                      <div className="truncate font-medium">{c.name}</div>
                      <div className="text-xs text-neutral-500 truncate">{FREQUENCIES.find((f) => f.value === c.frequency)?.label}</div>
                    </Link>
                    <div className="text-right flex items-center gap-2 ml-2">
                      <div className="font-mono-kpi text-xs font-bold text-[#FF5A00]">{(c.price_uf || 0).toLocaleString("es-CL", { maximumFractionDigits: 2 })} UF</div>
                      {scheduledIds.has(c.customer_id) ? (
                        <CalendarCheck className="w-4 h-4 text-[#00C853]" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-neutral-300" />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {m.expected.length > 0 && (
            <div className="px-4 py-2 border-t border-neutral-100 bg-neutral-50/50 flex items-center justify-between text-xs">
              <span className="text-neutral-500">Total esperado</span>
              <span className="font-mono-kpi font-bold text-[#FF5A00]">{totalUf.toLocaleString("es-CL", { maximumFractionDigits: 2 })} UF</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function BillingCyclePage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [showAuto, setShowAuto] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = () => api.get(`/billing-cycle?year=${year}`).then(({ data }) => {
    setData(data);
    // Initially expand only the current month
    const currentMonth = new Date().getMonth() + 1;
    setExpanded({ [currentMonth]: year === new Date().getFullYear() });
  });
  useEffect(() => { load(); }, [year]);

  if (!data) return <PageContainer><div className="font-mono-kpi text-xs text-neutral-500">Cargando…</div></PageContainer>;

  const currentMonth = new Date().getMonth();
  const totalExpected = data.months.reduce((a, b) => a + b.expected.length, 0);
  const totalScheduled = data.months.reduce((a, b) => a + b.scheduled.length, 0);
  const allExpanded = Object.values(expanded).every(Boolean) && Object.keys(expanded).length === 12;

  const expandAll = () => {
    if (allExpanded) setExpanded({});
    else setExpanded(Object.fromEntries(data.months.map((m) => [m.month, true])));
  };

  return (
    <PageContainer>
      <PageHeader
        kicker={`${year} · ${totalExpected} flujos esperados · ${totalScheduled} agendados`}
        title="Ciclo de servicios"
        subtitle="Vista anual que muestra qué clientes deben recibir mantención cada mes según su periodicidad contratada."
        actions={
          <div className="flex items-center gap-2">
            <button data-testid="billing-prev-year" onClick={() => setYear(year - 1)} className="p-2 hover:bg-neutral-100 border border-neutral-200"><ChevronLeft className="w-4 h-4" /></button>
            <div className="font-mono-kpi text-sm font-bold px-3">{year}</div>
            <button data-testid="billing-next-year" onClick={() => setYear(year + 1)} className="p-2 hover:bg-neutral-100 border border-neutral-200"><ChevronRight className="w-4 h-4" /></button>
            <SecondaryButton data-testid="billing-today" onClick={() => setYear(new Date().getFullYear())}>Hoy</SecondaryButton>
            <SecondaryButton data-testid="billing-expand-all" onClick={expandAll}>{allExpanded ? "Contraer todo" : "Expandir todo"}</SecondaryButton>
            <PrimaryButton data-testid="billing-auto-schedule" onClick={() => setShowAuto(true)}>
              <Route className="w-3.5 h-3.5 inline mr-2 -mt-0.5" /> Repartir ruta del mes
            </PrimaryButton>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.months.map((m) => (
          <MonthCard
            key={m.month}
            m={m}
            year={year}
            isCurrent={m.month - 1 === currentMonth && year === new Date().getFullYear()}
            expanded={!!expanded[m.month]}
            onToggle={() => setExpanded((prev) => ({ ...prev, [m.month]: !prev[m.month] }))}
          />
        ))}
      </div>

      <div className="mt-6 flex items-center gap-6 text-xs text-neutral-500">
        <div className="inline-flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-[#00C853]" /> Servicio agendado en calendario</div>
        <div className="inline-flex items-center gap-2"><AlertCircle className="w-4 h-4 text-neutral-300" /> Falta agendar día</div>
      </div>

      {showAuto && <AutoScheduleModal year={year} defaultMonth={currentMonth + 1} onClose={() => setShowAuto(false)} onDone={load} />}
    </PageContainer>
  );
}
